import { useMemo, useRef, useState } from 'react';

import { MEDIA_SM, useMediaQuery } from 'approot/shared/style';
import ActivitySheet from 'approot/activity-builder/components/shared/sheet/activity-sheet';
import SubActivityView from 'approot/activity-builder/components/sub-activity/sub-activity-view';
import {
  useInteractiveAudio,
  useInteractiveModal,
} from '../../shared/interactive-response-shared.hooks';
import { InteractiveResponseContainer } from '../../interactive-response.style';
import {
  RESPONSE_FREE,
  ViewProps,
} from '../interactive-response-categorise.constants';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { getComponentSelectorId } from 'approot/activity-builder/components/shared/logic/get-component-selector-id';
import ClassroomAnswerTabs, {
  QUESTION_VIEW,
  SUGGESTED_VIEW,
} from 'approot/activity-builder/classroom/classroom-answer-tabs';
import { hasNoRichTextData } from 'approot/activity-builder/components/suggested-answers/view/suggested-answer-view.utils';
import {
  ActivitySheetInnerContent,
  ActivitySheetInnerMax,
} from 'approot/activity-builder/components/shared/sheet/activity-sheet.style';
import { getSubActivityInstructionAsText } from 'approot/activity-builder/components/sub-activity/sub-activity.utils';
import ComponentOverlaySample from 'approot/activity-builder/components/shared/component-overlay/component-overlay-sample';
import InteractiveResponseCategorisePlayPreview from '../play/interactive-response-categorise-play-preview';
import InteractiveResponseCategoriseLargeView from '../interactive-response-categorise-large-view';
import InteractiveResponseCategoriseSmallView from '../interactive-response-categorise-small-view';
import InteractiveResponseSuggestedAnswers from 'approot/activity-builder/classroom/interactive-response/interactive-response-suggested-answers';
import { SampleProps } from 'approot/activity-builder/components/sample/activity-sample.constants';

const InteractiveResponseCategoriseViewSample = (
  props: ViewProps & SampleProps
) => {
  const {
    subActivityProperties,
    suggestedAnswerProperties,
    properties,
    banner,
    id,
  } = props;

  const isMediaUpSm = useMediaQuery(MEDIA_SM);
  const { isPlaying, startGame, finishGame } = useInteractiveModal();
  const { isVisible, openModal, closeModal } = useSimpleModalState();

  const [tab, setTab] = useState<string>(QUESTION_VIEW);

  const closeModalCallbackRef = useRef<Function | undefined>(undefined);

  const { toggleAudio, playAudio, playClick, playStart } =
    useInteractiveAudio();

  const isFreeResponse =
    properties.interactiveResponseCategorise.answerType === RESPONSE_FREE;

  const showSuggestedAnswers = useMemo(
    () => !isFreeResponse && !hasNoRichTextData(suggestedAnswerProperties),
    [isFreeResponse, suggestedAnswerProperties]
  );

  return (
    <>
      <SubActivityView subActivityDetails={props.subActivityProperties}>
        <InteractiveResponseContainer id={getComponentSelectorId(props.id)}>
          <InteractiveResponseCategorisePlayPreview {...props} shouldShuffle />
          <ComponentOverlaySample
            ariaLabel={`Click to start Grouping response: ${
              getSubActivityInstructionAsText(
                subActivityProperties?.instructions
              ) || properties.interactiveResponseCategorise.title
            }`}
            onClick={() => {
              openModal();
              startGame();
            }}
            data-selector="btn-start"
          />
        </InteractiveResponseContainer>
      </SubActivityView>
      {isVisible && (
        <ActivitySheet
          isVisible
          title={props.properties.interactiveResponseCategorise.title || ''}
          hasBackgroundColor={tab === QUESTION_VIEW}
          onClose={() => {
            if (closeModalCallbackRef.current) {
              closeModalCallbackRef.current();
              closeModalCallbackRef.current = undefined;
            } else {
              setTab(QUESTION_VIEW);
              closeModal();
            }
          }}
          tabsContent={
            <ClassroomAnswerTabs
              isPublished
              canClearModelledResponse={false}
              includeIndividualAnswers={false}
              includeClassResponse={false}
              includeSuggestedAnswers={showSuggestedAnswers}
              tab={tab}
              onSelect={option => setTab(option)}
              componentId={id}
              onCloseActivitySheet={() => {
                if (closeModalCallbackRef.current) {
                  closeModalCallbackRef.current();
                  closeModalCallbackRef.current = undefined;
                } else {
                  setTab(QUESTION_VIEW);
                  closeModal();
                }
              }}
            />
          }
          banner={banner}
        >
          {tab === QUESTION_VIEW && (
            <>
              {isMediaUpSm ? (
                <InteractiveResponseCategoriseLargeView
                  {...props}
                  isPlaying={isPlaying}
                  finishGame={finishGame}
                  showAnswer
                  onAnswerAttempt={() => {}}
                  attemptAnswers={undefined}
                  setCloseModalCallback={callback => {
                    closeModalCallbackRef.current = callback;
                  }}
                />
              ) : (
                <InteractiveResponseCategoriseSmallView
                  {...props}
                  isPlaying={isPlaying}
                  finishGame={finishGame}
                  playStart={playStart}
                  playClick={playClick}
                  showAnswer
                  playAudio={playAudio}
                  toggleAudio={toggleAudio}
                  onAnswerAttempt={() => {}}
                  attemptAnswers={undefined}
                />
              )}
            </>
          )}
          {tab === SUGGESTED_VIEW && (
            <ActivitySheetInnerContent id="content">
              <ActivitySheetInnerMax>
                <InteractiveResponseSuggestedAnswers
                  {...props}
                  component={props}
                />
              </ActivitySheetInnerMax>
            </ActivitySheetInnerContent>
          )}
        </ActivitySheet>
      )}
    </>
  );
};
export default InteractiveResponseCategoriseViewSample;
